import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import type { ApplicationProgress } from '../../utils/application-progress';
import { PersonAvatar } from './PersonAvatar';

/** Section label above the shared closing cards. Same chip as the person switcher. */
export function GroupTasksMarker({ progress }: { progress: ApplicationProgress }) {
  return (
    <div className="flex w-max shrink-0 items-center gap-3 rounded-2xl border border-gray-900 bg-gray-900 px-3 py-2.5 text-left text-white shadow-bento">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
        <Users size={18} aria-hidden />
      </span>
      <span className="flex flex-col">
        <span className="flex items-baseline justify-between gap-2">
          <span className="whitespace-nowrap text-sm font-semibold">Group tasks</span>
          <span className="shrink-0 text-xs tabular-nums text-white/80">{progress.percent}%</span>
        </span>
        <span
          className="mt-2 block h-2 overflow-hidden rounded-full bg-white/25"
          role="progressbar"
          aria-valuenow={progress.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Group tasks progress"
        >
          <span
            className="block h-full rounded-full bg-emerald-500 transition-[width] duration-500 ease-out"
            style={{ width: `${progress.percent}%` }}
          />
        </span>
      </span>
    </div>
  );
}

export interface ProspectPersonSwitcherPerson {
  id: string;
  displayName: string;
  isViewer: boolean;
  headshotDocumentId: string | null;
}

interface ProspectPersonSwitcherProps {
  people: ProspectPersonSwitcherPerson[];
  selectedId: string;
  progressForPerson: (personId: string) => ApplicationProgress;
  onSelect: (personId: string) => void;
}

export function ProspectPersonSwitcher({
  people,
  selectedId,
  progressForPerson,
  onSelect,
}: ProspectPersonSwitcherProps) {
  const measureRef = useRef<HTMLDivElement>(null);
  const ordered = useMemo(
    () => [
      ...people.filter((person) => person.isViewer),
      ...people.filter((person) => !person.isViewer),
    ],
    [people],
  );
  const namesKey = ordered.map((person) => person.displayName).join('\0');
  const [textWidth, setTextWidth] = useState(0);

  useLayoutEffect(() => {
    const root = measureRef.current;
    if (!root) return;
    const nameWidth = Math.max(
      0,
      ...[...root.querySelectorAll<HTMLElement>('[data-name]')].map((element) => element.offsetWidth),
    );
    const percentWidth = root.querySelector<HTMLElement>('[data-percent]')?.offsetWidth ?? 0;
    setTextWidth(nameWidth + percentWidth + 8);
  }, [namesKey]);

  return (
    <div className="relative flex flex-wrap gap-3">
      <div ref={measureRef} className="pointer-events-none invisible absolute whitespace-nowrap" aria-hidden>
        {ordered.map((person) => (
          <span key={person.id} data-name className="text-sm font-semibold">{person.displayName}</span>
        ))}
        <span data-percent className="text-xs">100%</span>
      </div>
      {ordered.map((person) => {
        const progress = progressForPerson(person.id);
        const selected = person.id === selectedId;
        return (
          <button
            key={person.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(person.id)}
            className={`flex w-max shrink-0 items-center gap-3 rounded-2xl border px-3 py-2.5 text-left shadow-bento ${
              selected
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-900 dark:border-white/10 dark:bg-gray-800 dark:text-white'
            }`}
          >
            <PersonAvatar documentId={person.headshotDocumentId} onDark={selected} />
            <span className="flex flex-col">
              <span
                className="flex items-baseline justify-between gap-2"
                style={textWidth > 0 ? { minWidth: textWidth } : undefined}
              >
                <span className="whitespace-nowrap text-sm font-semibold">{person.displayName}</span>
                <span className={`shrink-0 text-xs tabular-nums ${selected ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                  {progress.percent}%
                </span>
              </span>
              <span
                className={`mt-2 block h-2 overflow-hidden rounded-full ${selected ? 'bg-white/25' : 'bg-gray-200 dark:bg-white/10'}`}
                role="progressbar"
                aria-valuenow={progress.percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${person.displayName} progress`}
              >
                <span
                  className="block h-full rounded-full bg-emerald-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${progress.percent}%` }}
                />
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
